import { Link } from "react-router";
import { ComparePage } from "./ComparePage";

// Angle: CLOSING THE LOOP / FACILITY COLLABORATION. The reader's problem is
// what happens after a profile is reviewed: prescriber response, DON sign-off,
// DON acknowledgment, and proof at survey. Nothing here describes
// Profiles 360 itself.
export function Profiles360Alternative() {
  return (
    <ComparePage
      slug="profiles-360-alternative"
      competitor="Profiles 360"
      title="Profiles 360 Alternative for Consultant Pharmacy | WeConsult Rx"
      description="Considering a Profiles 360 alternative? WeConsult Rx carries each recommendation from medication profile review to prescriber response, DON sign-off, audit trail, and invoice."
      h1="A Profiles 360 alternative that closes the loop after the review."
      eyebrow="Profiles 360 alternative"
      answer="Considering an alternative to Profiles 360? WeConsult Rx is a consultant pharmacy platform that carries each recommendation past the medication profile review to a prescriber response, Director of Nursing sign-off, and a time-stamped audit record. Facilities get a portal to sign and follow up, and the consultant pharmacist invoices from the same closed reviews."
      criteriaHeading="Where recommendations usually stall, and what to check"
      criteriaLede="The review is rarely the slow part. The follow-through is. Test each of these with a real month of recommendations."
      criteria={[
        { label: "Prescriber response capture", detail: "Each recommendation records accept, modify, or decline with a reason, so an unanswered item is visible instead of lost in a fax queue." },
        { label: "A portal for the facility", detail: "Prescribers, DONs, and facility staff sign and respond through a secure facility portal without needing a full platform seat." },
        { label: "DON review and acknowledgment", detail: "The Director of Nursing reviews the month's responses on a worksheet and gives a final acknowledgment, recorded with date and signer." },
        { label: "Reassignment when a prescriber changes", detail: "Open recommendations can be reassigned to the covering provider rather than re-created by hand." },
        { label: "An audit trail you never assemble", detail: "Every step is time-stamped in an append-only log aligned to F756 and F757, ready to show a surveyor." },
        { label: "Invoices from closed work", detail: "Once the month's reviews are closed, the facility invoice is generated from them on your schedule." },
      ]}
      sections={[
        {
          heading: "What the facility sees",
          body: (
            <>
              <p>
                Consultant pharmacy is a shared job. WeConsult Rx gives each facility its own view of the work the
                pharmacist completed for them:
              </p>
              <ul>
                <li>Recommendation bundles waiting for prescriber or DON signature.</li>
                <li>Responses already received and items still awaiting a prescriber response.</li>
                <li>Drug recall notices that match medications on hand, plus med-cart inspection and controlled-substance destruction records.</li>
              </ul>
              <p>
                That shared view is what turns a monthly report into a closed loop. See how sign-off works on the{" "}
                <Link to="/features">features page</Link>.
              </p>
            </>
          ),
        },
        {
          heading: "Proving the loop closed at survey",
          body: (
            <>
              <p>
                Surveyors look for evidence that the pharmacist identified an irregularity, reported it to the
                attending physician and DON, and that it was acted on. Because every one of those steps happens inside
                WeConsult Rx, the evidence already exists with dates and signers attached.
              </p>
              <p>
                Read how this maps to the medication F-tags on the{" "}
                <Link to="/survey-readiness">survey readiness page</Link>, or review our{" "}
                <Link to="/security">security</Link> and <Link to="/hipaa">HIPAA</Link> controls.
              </p>
            </>
          ),
        },
      ]}
      faqs={[
        {
          q: "What is a good alternative to Profiles 360 for consultant pharmacy?",
          a: "WeConsult Rx is an alternative that focuses on closing the loop: each recommendation moves from review to prescriber response, DON review and acknowledgment, and an audit record, with a facility portal and invoicing in the same platform.",
        },
        {
          q: "Can facility staff sign without a full software license?",
          a: "Yes. Prescribers, DONs, and facility staff use a secure facility portal to review, respond to, and e-sign recommendations.",
        },
        {
          q: "How does WeConsult Rx track prescriber responses?",
          a: "Every recommendation records whether the prescriber accepted, modified, or declined it, with a reason and a time-stamped signature. Unanswered items stay open in a follow-up queue.",
        },
        {
          q: "What documentation does a surveyor see?",
          a: "A dated record of each irregularity identified, who it was reported to, the prescriber's response, and the signatures, kept in an append-only audit trail aligned to F756 and F757.",
        },
        {
          q: "Does WeConsult Rx handle drug recalls?",
          a: "Yes. Recall notices are matched against facility medications and surfaced to the pharmacist and the facility portal.",
        },
      ]}
      related={[
        { to: "/survey-readiness", label: "Survey readiness", desc: "How the loop maps to medication F-tags." },
        { to: "/features", label: "Features", desc: "E-signature, report bundles, and invoicing." },
        { to: "/trust", label: "Trust center", desc: "Security, HIPAA, and BAA details." },
      ]}
      cta={{
        title: "See a recommendation go from flag to signature",
        body: "Book a demo on a synthetic facility to watch the full loop, or start with a free first-facility review.",
      }}
    />
  );
}
